import {
  Activity,
  Atom,
  Beaker,
  ClipboardList,
  Dna,
  Droplet,
  FlaskConical,
  HeartPulse,
  Microscope,
  Pill,
  Pipette,
  Stethoscope,
  Syringe,
  TestTubes,
  Thermometer,
} from 'lucide-react';
import type { ReactNode } from 'react';

type Spot = {
  Icon: typeof Dna;
  x: number;
  y: number;
  size: number;
  turn: number;
  tone: 'rose' | 'plum';
  small?: true;
};

// Placed by hand so the page looks the same every time, with more room around the sign-in card.
const SPOTS: Spot[] = [
  { Icon: FlaskConical, x: 5, y: 9, size: 58, turn: -14, tone: 'rose' },
  { Icon: Microscope, x: 18, y: 24, size: 52, turn: 8, tone: 'plum' },
  { Icon: TestTubes, x: 8, y: 47, size: 60, turn: -8, tone: 'rose' },
  { Icon: Dna, x: 20, y: 70, size: 54, turn: 18, tone: 'plum' },
  { Icon: Droplet, x: 6, y: 86, size: 40, turn: -10, tone: 'rose' },
  { Icon: Syringe, x: 30, y: 90, size: 48, turn: 24, tone: 'rose', small: true },
  { Icon: Pipette, x: 33, y: 6, size: 46, turn: -26, tone: 'plum', small: true },
  { Icon: Stethoscope, x: 47, y: 3, size: 56, turn: 6, tone: 'rose', small: true },
  { Icon: Activity, x: 62, y: 92, size: 54, turn: 0, tone: 'plum', small: true },
  { Icon: Beaker, x: 70, y: 6, size: 50, turn: 14, tone: 'rose', small: true },
  { Icon: Atom, x: 90, y: 10, size: 56, turn: -12, tone: 'plum' },
  { Icon: HeartPulse, x: 78, y: 26, size: 50, turn: 10, tone: 'rose' },
  { Icon: Pill, x: 93, y: 40, size: 42, turn: -30, tone: 'rose' },
  { Icon: ClipboardList, x: 82, y: 58, size: 54, turn: 8, tone: 'plum' },
  { Icon: Thermometer, x: 92, y: 74, size: 52, turn: -8, tone: 'rose' },
  { Icon: FlaskConical, x: 75, y: 88, size: 48, turn: 16, tone: 'plum' },
  { Icon: Droplet, x: 14, y: 36, size: 30, turn: 0, tone: 'plum', small: true },
  { Icon: Atom, x: 3, y: 66, size: 34, turn: 20, tone: 'plum', small: true },
  { Icon: Pill, x: 55, y: 90, size: 34, turn: 40, tone: 'rose', small: true },
  { Icon: Beaker, x: 96, y: 92, size: 36, turn: -18, tone: 'rose', small: true },
];

/**
 * A soft rose page with medical-lab line icons scattered on it, behind the sign-in card. Purely
 * decoration: it is hidden from screen readers and never takes a click.
 */
export function LabBackdrop({ children }: { children: ReactNode }) {
  return (
    <div className="relative isolate grid min-h-full place-items-center overflow-hidden bg-[#fff1f5] p-4 sm:p-6">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 select-none">
        {SPOTS.map((s, i) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: the list never changes
            key={i}
            className={`absolute ${s.small ? 'max-sm:hidden' : ''}`}
            style={{
              left: `${s.x}%`,
              top: `${s.y}%`,
              transform: `translate(-50%, -50%) rotate(${s.turn}deg)`,
            }}
          >
            <s.Icon
              size={s.size}
              strokeWidth={1.25}
              className={s.tone === 'rose' ? 'text-accent/30' : 'text-brand/15'}
            />
          </span>
        ))}
      </div>
      {children}
    </div>
  );
}
